'use client'

// Selector de "si algo no está disponible" (spec sustituciones §4.2, Task 4).
// Lo usan SubstitutionsStep (preferencia de la orden entera, su propio paso tras
// el carrito), PaymentStep (solo en mesa por QR) y SubstitutionLineSheet
// (preferencia de una línea, compact=true).

import { useTranslations } from 'next-intl'
import { Repeat, Undo2, MessageSquare } from 'lucide-react'
import type { SubstitutionPref } from '@/lib/online-orders/substitutionPrefs'

type Props = {
  /** undefined = ninguna opción presionada (p.ej. la línea hereda la preferencia de la orden). */
  value: SubstitutionPref | undefined
  onChange: (v: SubstitutionPref) => void
  /** El cliente dejó un teléfono usable y no rechazó SMS — sin esto "avisarme" no es una opción real. */
  contactAllowed: boolean
  /**
   * Todavía no hay teléfono porque el cliente aún no llegó a «Tus datos» (el
   * carrito va antes). "Preguntarme primero" queda habilitado y se avisa que el
   * teléfono se pide después. Si al final no deja teléfono o marca «No quiero
   * recibir SMS», el servidor la cambia a "algo parecido" y la confirmación lo
   * dice (resolveSubstitutionPrefs, aviso contact_needs_sms).
   */
  contactPhonePending?: boolean
  accentColor: string
  compact?: boolean
}

const ICONS = { similar: Repeat, refund: Undo2, contact: MessageSquare } as const

export function SubstitutionPrefSelector({ value, onChange, contactAllowed, contactPhonePending = false, accentColor, compact }: Props) {
  const t = useTranslations('public.onlineOrder.checkout.substitution')
  const options: SubstitutionPref[] = ['similar', 'refund', 'contact']
  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {!compact && <p className="text-sm font-semibold text-gray-900">{t('title')}</p>}
      <div className="space-y-2">
        {options.map((opt) => {
          const Icon = ICONS[opt]
          const selected = value === opt
          const disabled = opt === 'contact' && !contactAllowed && !contactPhonePending
          return (
            <button
              key={opt}
              type="button"
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => onChange(opt)}
              className="w-full flex items-center gap-3 rounded-2xl border-[1.5px] p-3.5 text-left transition disabled:opacity-50"
              style={{
                borderColor: selected ? accentColor : '#E5E7EB',
                backgroundColor: selected ? `${accentColor}0D` : '#FFFFFF',
              }}
            >
              <Icon className="h-5 w-5 shrink-0" style={{ color: selected ? accentColor : '#6B7280' }} aria-hidden />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-gray-900">{t(opt)}</span>
                <span className="block text-xs text-gray-500">{t(`${opt}Hint`)}</span>
              </span>
            </button>
          )
        })}
      </div>
      {contactPhonePending ? (
        value === 'contact' && <p className="text-xs text-gray-500">{t('contactPhoneLater')}</p>
      ) : (
        !contactAllowed && <p className="text-xs text-gray-500">{t('contactNeedsSms')}</p>
      )}
    </div>
  )
}
